import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule
} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AppService} from '@services/app.service';
import {UserService} from '@services/user.service';
import {FarmType} from '@/common/Enum/FarmType.Enum';

@Component({
    selector: 'app-user-profile-settings',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './user-profile-settings.component.html',
    styleUrl: './user-profile-settings.component.scss'
})
export class UserProfileSettingsComponent implements OnInit {
    public profileForm: FormGroup;
    public passwordForm: FormGroup;
    public showOldPassword: boolean = false;
    public showNewPassword: boolean = false;
    public showConfirmPassword: boolean = false;
    public isLoading: boolean = false;
    public userDetail: any;
    public farmTypes = FarmType;
    public farmTypeOptions = [
        {id: FarmType.FarmOwner, name: 'Farm Owner'},
        {id: FarmType.FarmManager, name: 'Farm Manager'}
    ];

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private appService: AppService,
        private userService: UserService
    ) {}

    ngOnInit(): void {
        this.initializeForms();
        this.loadUserData();
    }

    initializeForms(): void {
        // Profile form
        this.profileForm = this.fb.group({
            title: ['', Validators.required],
            name: ['', [Validators.required, Validators.minLength(2)]],
            phone: ['', [Validators.required, Validators.pattern(/^05\d{8}$/)]],
            email: [
                '',
                [
                    Validators.required,
                    Validators.pattern(
                        '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
                    )
                ]
            ]
        });

        // Password form
        this.passwordForm = this.fb.group({
            oldPassword: ['', Validators.required],
            newPassword: [
                '',
                [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.pattern(
                        '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+=]).{8,}$'
                    )
                ]
            ],
            confirmPassword: ['', Validators.required]
        });
    }

    loadUserData(): void {
        const userDetailStr = localStorage.getItem('UserDetail');
        if (userDetailStr) {
            this.userDetail = JSON.parse(userDetailStr);
        } else {
            this.userDetail = null;
        }

        if (this.userDetail && this.userDetail.id) {
            this.isLoading = true;
            this.userService.getUserProfileById(this.userDetail.id).subscribe({
                next: (user) => {
                    this.userDetail = user;
                    this.profileForm.patchValue({
                        title: Number(user.title) || 0,
                        name: user.name || '',
                        phone: user.phone || '',
                        email: user.email || ''
                    });
                    this.isLoading = false;
                },
                error: () => {
                    this.toastr.error('Failed to load user profile');
                    this.isLoading = false;
                }
            });
        }
    }

    togglePasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
        switch (field) {
            case 'old':
                this.showOldPassword = !this.showOldPassword;
                break;
            case 'new':
                this.showNewPassword = !this.showNewPassword;
                break;
            case 'confirm':
                this.showConfirmPassword = !this.showConfirmPassword;
                break;
        }
    }

    onUpdateProfile(): void {
        if (this.profileForm.invalid) {
            this.profileForm.markAllAsTouched();
            this.toastr.error('Please fill all required fields correctly');
            return;
        }

        this.isLoading = true;

        const updateModel = {
            id: this.userDetail.id,
            title: this.profileForm.value.title,
            name: this.profileForm.value.name,
            phone: this.profileForm.value.phone,
            email: this.profileForm.value.email
        };

        this.userService.updateUserProfile(updateModel).subscribe({
            next: (response) => {
                this.isLoading = false;
                this.toastr.success('Profile updated successfully');
                // Update local storage
                this.userDetail = {...this.userDetail, ...updateModel};
                localStorage.setItem(
                    'UserDetail',
                    JSON.stringify(this.userDetail)
                );
            },
            error: () => {
                this.isLoading = false;
                this.toastr.error('Failed to update profile');
            }
        });
    }

    // onUpdatePassword(): void {
    //     if (this.passwordForm.invalid) {
    //         this.passwordForm.markAllAsTouched();
    //         this.toastr.error('Please fill all required fields correctly');
    //         return;
    //     }

    //     const {oldPassword, newPassword, confirmPassword} =
    //         this.passwordForm.value;

    //     // Validate old password
    //     if (oldPassword !== this.mockUser.password) {
    //         this.toastr.error('Incorrect old password');
    //         return;
    //     }

    //     // Validate password match
    //     if (newPassword !== confirmPassword) {
    //         this.toastr.error('New password and confirm password do not match');
    //         return;
    //     }

    //     // Validate password criteria
    //     const passwordRegex =
    //         /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+=]).{8,}$/;
    //     if (!passwordRegex.test(newPassword)) {
    //         this.toastr.error(
    //             'Password must include upper and lower letters, a number, special character, and be at least 8 characters long'
    //         );
    //         return;
    //     }

    //     this.isLoading = true;

    //     // Simulate API call
    //     setTimeout(() => {
    //         this.isLoading = false;
    //         this.passwordForm.reset();
    //         this.toastr.success('Password updated successfully');
    //     }, 1000);
    // }

    // Getters for form controls
    get profileFormControls() {
        return this.profileForm.controls;
    }

    get passwordFormControls() {
        return this.passwordForm.controls;
    }
}
